import * as React from 'react';

import { Alert } from '@lightspeed/flame/Alert';
import { Bone } from '@lightspeed/flame/Bone';
import { Card, CardSection } from '@lightspeed/flame/Card';
import { Box, Flex, flameTheme } from '@lightspeed/flame/Core';
import { Dialog } from '@lightspeed/flame/Dialog';
import { Divider } from '@lightspeed/flame/Divider';
import { AnnotatedLayout } from '@lightspeed/flame/Layout';
import { Switch } from '@lightspeed/flame/Switch';
import { Text, TextLink } from '@lightspeed/flame/Text';

import { translate } from 'pages/utils/translate';
import Monitoring from 'shared_modules/Monitoring';

import { useTipSettings } from './store';
import { LoadingIndicator } from './store/type';

const tipDialogText = {
  on: {
    title: translate('Turn on tipping?'),
    body: translate(
      'Tipping will automatically be enabled on all registers, but you can customize which registers accept tips.'
    ),
    confirmText: translate('Turn on'),
  },
  off: {
    title: translate('Turn off tipping?'),
    body: translate(
      'You are about to turn off tipping. You will no longer be able to accept tips on any registers.'
    ),
    confirmText: translate('Turn off'),
  },
};

const TipToggleSwitch = ({ setShowAlert }: { setShowAlert: (val: boolean) => void }) => {
  const { tipToggle, updateTipConfiguration, tipOptions } = useTipSettings();

  const [isDialogOpen, setDialogOpen] = React.useState(false);
  const [isLoading, setLoading] = React.useState(false);
  const selectedText = tipToggle ? tipDialogText.off : tipDialogText.on;
  const onConfirm = async () => {
    setLoading(true);
    try {
      await updateTipConfiguration({ tipToggle: !tipToggle, tipOptions });
      if (!tipToggle) {
        setShowAlert(true);
      }
    } catch (e) {
      Monitoring.NRError(e, 'ReactViewMainToggleSwitch', {
        errorName: 'tip.error.update',
        description: 'Unable to update the tip settings when toggling the feature.',
      });
    } finally {
      setDialogOpen(false);
      setLoading(false);
    }
  };
  return (
    <>
      <Switch
        checked={tipToggle}
        aria-label="Tip toggle"
        onChange={() => {
          setDialogOpen(true);
        }}
      />
      <Dialog
        title={selectedText.title}
        message={selectedText.body}
        isOpen={isDialogOpen}
        onCancel={() => {
          setDialogOpen(false);
        }}
        isLoading={isLoading}
        onConfirm={onConfirm}
        cancelText={translate('Cancel')}
        confirmText={selectedText.confirmText}
        type="secondary"
      />
    </>
  );
};

export const MainToggleSwitch = () => {
  const { tipToggle, loadingState } = useTipSettings();
  const [showAlert, setShowAlert] = React.useState(false);
  const goToLocation = () => {
    window.merchantos.utils.openUrl('/?name=admin.listings.shops&form_name=listing&__sort_dir=ASC');
  };

  React.useEffect(() => {
    let isMounted = true;
    if (showAlert) {
      setTimeout(() => {
        if (isMounted) {
          setShowAlert(false);
        }
      }, 3000);
    }
    return () => {
      isMounted = false;
    };
  }, [showAlert]);

  return (
    <>
      {showAlert && (
        <Alert
          type="success"
          aria-label="tip config enabled success info"
          mb={6}
          mt={3}
          title={translate(`Success`)}
        >
          {translate('You’ve turned on tipping.')}
        </Alert>
      )}
      <AnnotatedLayout
        title={translate(`Tipping`)}
        description={translate(`Allow customers to add a tip at checkout.`)}
        renderExtras={
          <TextLink
            href="https://retail-support.lightspeedhq.com/hc/articles/4402387870875"
            target="_blank"
          >
            {translate(`Learn more`)}
          </TextLink>
        }
      >
        <Card>
          {loadingState === LoadingIndicator.pending ? (
            <CardSection>
              <Box my={2} mx={2} aria-label="loading box">
                <Box width="30%" mb={2}>
                  <Bone height="30px" />
                </Box>
                <Box width="70%">
                  <Bone height="18px" />
                </Box>
              </Box>
            </CardSection>
          ) : (
            <>
              <CardSection>
                <Flex my={2} mx={2} flexDirection="column">
                  <Flex mb={2} justifyContent="space-between">
                    <Text fontWeight="bold" color={flameTheme.colors.black}>
                      {translate(`Tipping`)}
                    </Text>
                    <TipToggleSwitch setShowAlert={setShowAlert} />
                  </Flex>
                  <Text color={flameTheme.colors['gray-600']}>
                    {translate('Give customers the option to tip on all of your registers.')}
                  </Text>
                </Flex>
              </CardSection>
              {tipToggle && (
                <>
                  <Divider />
                  <CardSection aria-label="register selection">
                    <Flex my={2} mx={2} flexDirection="column">
                      <Text mb={2} fontWeight="bold" color={flameTheme.colors.black}>
                        {translate('Registers')}
                      </Text>
                      <Text mb={1} color={flameTheme.colors['gray-600']}>
                        {translate('Select which registers will prompt the customer to add a tip.')}
                      </Text>
                      <TextLink aria-label="go to location link" onClick={goToLocation}>
                        {translate('Go to Locations')}
                      </TextLink>
                    </Flex>
                  </CardSection>
                </>
              )}
            </>
          )}
        </Card>
      </AnnotatedLayout>
    </>
  );
};
